<template>
  <div class="attachments" :class="alignClass" role="list">
    <div
      v-for="att in view"
      :key="att.id"
      role="listitem"
      class="chip"
      :class="[sizeClass, att.kind === 'image' ? 'chip-image' : 'chip-document']"
      :title="att.kind === 'document' ? att.name : undefined"
    >
      <template v-if="att.kind === 'image'">
        <img :src="att.previewUrl" :alt="att.name" class="thumb" />
      </template>

      <template v-else>
        <div class="doc-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path
              fill="currentColor"
              d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 1v5h5"
            />
          </svg>
        </div>
        <div class="doc-meta">
          <div class="name">{{ truncatedName(att.name) }}</div>
        </div>
      </template>

      <button
        v-if="showRemoveEffective"
        class="remove"
        :class="removeAlignClass"
        @click.stop="emit('remove', att.id, att.name)"
        :aria-label="`Remove ${att.name}`"
        :title="`Remove ${att.name}`"
      >
        <img
          class="remove-icon"
          :src="appStore.currentMode() === DisplayMode.Light ? xCloseLight : xCloseDark"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import xCloseLight from '../assets/icons/light/icon-x-close.svg'
import xCloseDark from '../assets/icons/dark/icon-x-close.svg'
import { useAppStore } from '../stores/appStore'
import { DisplayMode } from '../types/app/displayMode'

export type Mode = 'composer' | 'message'
export type ChipSize = 'sm' | 'md' | 'lg'
export type AttachmentKind = 'image' | 'document'
export type AlignSide = 'left' | 'right'

export interface MessageAttachment {
  id?: string
  name: string
  type: AttachmentKind
  thumbnailUrl?: string
  file?: File
  mime?: string
  sizeBytes?: number
}

const appStore = useAppStore()

const props = defineProps<{
  items: MessageAttachment[]
  mode?: Mode
  size?: ChipSize
  showRemove?: boolean
  align?: AlignSide
}>()

const emit = defineEmits<{
  (e: 'remove', id: string, name: string): void
  (e: 'error', err: unknown): void
}>()

const mode = computed<Mode>(() => props.mode ?? 'composer')

const side = computed<AlignSide>(
  () => props.align ?? (mode.value === 'composer' ? 'right' : 'left')
)

const alignClass = computed(() => (side.value === 'right' ? 'align-right' : 'align-left'))

const removeAlignClass = computed(() => (side.value === 'right' ? 'remove--end' : 'remove--start'))

const showRemoveEffective = computed<boolean>(() =>
  typeof props.showRemove === 'boolean' ? props.showRemove : mode.value === 'composer'
)

const sizeClass = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'chip--sm'
    case 'lg':
      return 'chip--lg'
    default:
      return 'chip--md'
  }
})

type AttachmentVM = {
  id: string
  kind: AttachmentKind
  name: string
  previewUrl?: string
}

const urlMap = ref(new Map<string, string>())

const view = computed<AttachmentVM[]>(() =>
  props.items.map((item, idx) => {
    const id = item.id ?? `${item.name}-${idx}`
    const kind = item.type
    const name = item.name
    let previewUrl = item.thumbnailUrl ?? ''
    if (kind === 'image' && !previewUrl && item.file) {
      previewUrl = urlMap.value.get(id) ?? ''
    }
    return { id, kind, name, previewUrl }
  })
)

watch(
  () => props.items,
  (newItems, oldItems) => {
    const oldIds = new Set((oldItems ?? []).map(a => a.id ?? `${a.name}-old`))
    const newIds = new Set((newItems ?? []).map(a => a.id ?? `${a.name}-new`))

    oldIds.forEach(id => {
      if (!newIds.has(id)) {
        const u = urlMap.value.get(id)
        if (u) {
          URL.revokeObjectURL(u)
          urlMap.value.delete(id)
        }
      }
    })

    newItems?.forEach((a, idx) => {
      if (a.type === 'image' && a.file && !a.thumbnailUrl) {
        const id = a.id ?? `${a.name}-${idx}`
        if (!urlMap.value.has(id)) {
          try {
            const u = URL.createObjectURL(a.file)
            urlMap.value.set(id, u)
          } catch (e) {
            emit('error', e)
          }
        }
      }
    })
  },
  { immediate: true, deep: true }
)

onBeforeUnmount(() => {
  urlMap.value.forEach(u => URL.revokeObjectURL(u))
  urlMap.value.clear()
})

function truncatedName(name: string, max = 32) {
  if (name.length <= max) return name
  const dot = name.lastIndexOf('.')
  if (dot > 0) {
    const base = name.slice(0, dot)
    const ext = name.slice(dot)
    const keep = Math.max(6, max - ext.length - 1)
    return base.slice(0, keep) + '…' + ext
  }
  return name.slice(0, max - 1) + '…'
}
</script>
<style scoped>
.attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-start;
}

.attachments.align-right {
  justify-content: flex-end;
}
.attachments.align-left {
  justify-content: flex-start;
}

.chip {
  position: relative;
  border: 1px solid var(--border-color);
  border-radius: 14px;
  background: var(--background-color);
  overflow: hidden;
}

.chip--sm.chip-image {
  width: 72px;
  height: 72px;
}
.chip--md.chip-image {
  width: 88px;
  height: 88px;
}
.chip--lg.chip-image {
  width: 120px;
  height: 120px;
}

.chip--sm.chip-document {
  height: 72px;
  padding: 8px 10px;
}
.chip--md.chip-document {
  height: 88px;
  padding: 10px 12px;
}
.chip--lg.chip-document {
  height: 120px;
  padding: 14px 16px;
  font-size: 16px;
}

.thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.chip-document {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 340px;
}

.attachments.align-right .chip-document {
  flex-direction: row-reverse;
}
.attachments.align-right .chip-document .doc-meta {
  text-align: right;
}

.doc-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, 0.06);
  color: var(--text-color);
  flex: 0 0 32px;
}

.doc-meta {
  min-width: 0;
}

.name {
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 220px;
  color: var(--text-color);
}
.chip--lg .name {
  font-size: 15px;
  max-width: 250px;
}

.remove {
  position: absolute;
  inset-block-start: 6px;
  inset-inline-end: 6px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 999px;
  background: var(--primary-color);
  color: var(--background-color);
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
  z-index: 2;
}
.remove:hover {
  opacity: 0.9;
}
.remove-icon {
  align-self: center;
  height: 10px;
  width: 10px;
  filter: brightness(0) invert(1);
}
</style>
